// Pure geometry for the Milestone Roadmap calendar: date <-> x mapping across the server's period
// columns (which may skip hidden periods), pill measurement, and greedy lane stacking.
//
// Dates are ISO "Y-m-d" strings handled as UTC day numbers, so no local offset shifts a day.

import { floatDaysLabel } from '../schedule/scheduleEntries';

import { formatDateShort } from 'Helpers/dateFormat';

export const COL_W = { days: 34, months: 160, quarters: 320, years: 560 };
export const GAP_W = 14;
export const LEFT_W = 320;

export const HEADER_GROUP_H = 22;
export const HEADER_PERIOD_H = 30;
export const HEADER_H = HEADER_GROUP_H + HEADER_PERIOD_H;

export const SOURCE_ROW_H = 40;
export const CATEGORY_ROW_H = 36;
export const SUBCATEGORY_ROW_H = 36;

export const LANE_H = 36;
export const ROW_PAD_Y = 10;
export const PILL_H = { expanded: 30, compact: 24 };
export const PILL_LANE_GAP = 6;
export const COMPACT_PILL_W = 160;
// Room a compact pill adds for its float label, whatever the number.
export const FLOAT_PILL_W = 56;

// Border, padding, icon and gaps around the pill text. Keep in step with RoadmapPill's classes.
const PILL_CHROME_W = 52;
// The gap before the float label (tw-gap-1.5).
const FLOAT_GAP_W = 6;

const DAY_MS = 86400000;

export function toDay(iso) {
    const [y, m, d] = String(iso).split('-').map(Number);

    return Math.round(Date.UTC(y, m - 1, d) / DAY_MS);
}

export function fromDay(day) {
    return new Date(day * DAY_MS).toISOString().slice(0, 10);
}

/**
 * Position the server's periods as equal-width columns. Narrower than the viewport, they stretch to
 * fill it; a hatched break of GAP_W follows every `gapAfter` column.
 */
export function layoutColumns(periods, viewportWidth, view) {
    const base = COL_W[view] || COL_W.quarters;
    const gaps = periods.filter((period) => period.gapAfter).length;
    const natural = periods.length * base + gaps * GAP_W;
    const width = periods.length && natural < viewportWidth ? (viewportWidth - gaps * GAP_W) / periods.length : base;

    let x = 0;
    const columns = periods.map((period) => {
        const column = { ...period, s: toDay(period.start), e: toDay(period.end), x, w: width };
        x += width + (period.gapAfter ? GAP_W : 0);

        return column;
    });

    return { columns, width: x };
}

// Index of the first column ending after `day`, or columns.length.
function columnIndexForDay(columns, day) {
    let low = 0;
    let high = columns.length;

    while (low < high) {
        const mid = (low + high) >> 1;

        if (columns[mid].e <= day) {
            low = mid + 1;
        } else {
            high = mid;
        }
    }

    return low;
}

// Index of the first column whose right edge is past `px`, or columns.length.
function columnIndexForX(columns, px) {
    let low = 0;
    let high = columns.length;

    while (low < high) {
        const mid = (low + high) >> 1;

        if (columns[mid].x + columns[mid].w <= px) {
            low = mid + 1;
        } else {
            high = mid;
        }
    }

    return low;
}

/**
 * A date's x across the columns. A date inside a hidden period sits at the start of the next kept
 * column; one past the end sits at the right edge.
 */
export function xOf(layout, iso) {
    const { columns, width } = layout;

    if (!columns.length || !iso) {
        return 0;
    }

    const day = toDay(iso);
    const index = columnIndexForDay(columns, day);

    if (index === columns.length) {
        return width;
    }

    const column = columns[index];

    if (day < column.s) {
        return column.x;
    }

    return column.x + ((day - column.s) / (column.e - column.s)) * column.w;
}

/**
 * The date at a timeline x. Inside a hatched break it is the start of the next column.
 */
export function dateAt(layout, px) {
    const { columns } = layout;

    if (!columns.length) {
        return null;
    }

    const index = columnIndexForX(columns, px);

    if (index === columns.length) {
        return fromDay(columns[columns.length - 1].e - 1);
    }

    const column = columns[index];
    const fraction = Math.min(1, Math.max(0, (px - column.x) / column.w));

    // The epsilon absorbs float error, so the x of a day's start reads back as that day.
    return fromDay(Math.min(column.e - 1, column.s + Math.floor(fraction * (column.e - column.s) + 1e-6)));
}

/**
 * The scrollLeft that puts a date at the timeline's left edge. Rounded up to a whole pixel, which
 * the browser keeps as is and which is still inside the day (a day is at least 1.5px wide), so
 * dateAt() of the result is the same date.
 */
export function scrollLeftFor(layout, iso) {
    return Math.ceil(xOf(layout, iso) - 1e-6);
}

/**
 * [first, last] indices of the columns intersecting [fromPx, toPx]; last < first when none do.
 */
export function columnRange(layout, fromPx, toPx) {
    const { columns } = layout;
    const first = columnIndexForX(columns, fromPx);
    let last = columnIndexForX(columns, toPx);

    if (last >= columns.length) {
        last = columns.length - 1;
    }

    return [first, last];
}

let measuringContext = null;

/**
 * Rendered pill width. Expanded pills are sized to their full text plus the inline date so they
 * never truncate; compact pills are a fixed width and truncate.
 */
export function measurePill(milestone, mode, fontFamily) {
    const hasFloat = milestone.float !== undefined && milestone.float !== null;

    if (mode === 'compact') {
        return COMPACT_PILL_W + (hasFloat ? FLOAT_PILL_W : 0);
    }

    if (!measuringContext) {
        measuringContext = document.createElement('canvas').getContext('2d');
    }

    measuringContext.font = `500 13px ${fontFamily}`;
    const nameWidth = measuringContext.measureText(milestone.name).width;
    measuringContext.font = `300 10px ${fontFamily}`;
    const dateWidth = measuringContext.measureText(formatDateShort(milestone.date)).width;
    let floatWidth = 0;

    if (hasFloat) {
        measuringContext.font = `600 10px ${fontFamily}`;
        floatWidth = FLOAT_GAP_W + measuringContext.measureText(floatDaysLabel(milestone.float)).width;
    }

    return Math.ceil(nameWidth + dateWidth + floatWidth + PILL_CHROME_W);
}

/**
 * Greedy lane assignment: each item (sorted by x) takes the first lane free at its start, with a
 * PILL_LANE_GAP gutter. Mutates `lane` onto each item and returns the lane count.
 */
export function assignLanes(items) {
    const laneEnds = [];

    items.forEach((item) => {
        let lane = laneEnds.findIndex((end) => end <= item.x - PILL_LANE_GAP);

        if (lane < 0) {
            lane = laneEnds.length;
            laneEnds.push(0);
        }

        laneEnds[lane] = item.x + item.w;
        item.lane = lane;
    });

    return laneEnds.length;
}

export function rowHeight(laneCount, mode, minHeight) {
    if (!laneCount) {
        return minHeight;
    }

    return Math.max(minHeight, 2 * ROW_PAD_Y + (laneCount - 1) * LANE_H + PILL_H[mode]);
}

export function pillTop(lane) {
    return ROW_PAD_Y + lane * LANE_H;
}

/**
 * First row index whose bottom edge is below `y` (rows carry `top` and `height`).
 */
export function rowIndexAt(rows, y) {
    let low = 0;
    let high = rows.length;

    while (low < high) {
        const mid = (low + high) >> 1;

        if (rows[mid].top + rows[mid].height <= y) {
            low = mid + 1;
        } else {
            high = mid;
        }
    }

    return low;
}
